import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import '../client/ClientPortal.css';
import TransporterLoginPage from './TransporterLoginPage';
import TransporterEIR from './TransporterEIR';
import TransporterInvoicing from './TransporterInvoicing';

// ── Auth helpers ──────────────────────────────────────────────────────────────
const TOKEN_KEY = 'tp_token';
const REFRESH_KEY = 'tp_refresh_token';
const USER_KEY = 'tp_user';

const tpGetHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
    'Content-Type': 'application/json',
});

// ── Navigation config ─────────────────────────────────────────────────────────
const NAV = [
    {
        group: 'EIR/Invoices',
        items: [
            { key: 'eir', label: 'EIR', icon: 'fa-solid fa-exchange-alt', path: 'eir', module: 'TRANSPORTER EIR' },
            { key: 'invoicing', label: 'Invoicing', icon: 'fa-solid fa-file-invoice-dollar', path: 'invoicing', module: 'TRANSPORTER INVOICING' },
        ],
    },
];

// ── Sidebar ───────────────────────────────────────────────────────────────────
const Sidebar = ({ collapsed, permissions }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path) => location.pathname.includes(`/transporter/${path}`);
    const canView = (module) => permissions && permissions[module]?.can_view !== false;

    const filteredNav = NAV.map(group => ({
        ...group,
        items: group.items.filter(item => canView(item.module))
    })).filter(group => group.items.length > 0);

    return (
        <aside className={`cp-sidebar${collapsed ? ' collapsed' : ''}`} style={{ background: '#123d1b' }}>
            <div className="cp-sidebar-logo">
                <i className="fa-solid fa-truck logo-icon" style={{ color: '#4ade80' }}></i>
                
            </div>
            <div className="cp-sidebar-logo">
                <span>K &amp; K Transporter Portal</span>
            </div>

            <nav className="cp-nav">
                {filteredNav.map(group => (
                    <div className="cp-nav-group" key={group.group}>
                        <div className="cp-nav-group-title">{group.group}</div>
                        {group.items.map(item => (
                            <div
                                key={item.key}
                                className={`cp-nav-item${isActive(item.path) ? ' active' : ''}`}
                                onClick={() => navigate(`/transporter/${item.path}`)}
                                title={collapsed ? item.label : undefined}
                            >
                                <i className={`${item.icon} nav-icon`}></i>
                                <span>{item.label}</span>
                            </div>
                        ))}
                    </div>
                ))}
            </nav>

            <div className="cp-sidebar-footer">
                <span className="company-name">K AND K COMPANY LIMITED</span>
                <span className="copyright">&copy; {new Date().getFullYear()}</span>
            </div>
        </aside>
    );
};

// ── Reset Password Modal ─────────────────────────────────────────────────────
const TransporterResetPasswordModal = ({ onClose, onSuccess }) => {
    const [current, setCurrent] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!current || !newPw || !confirm) {
            setError('All fields are required.');
            return;
        }
        if (newPw !== confirm) {
            setError('New passwords do not match.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch('/api/transporter/change-password/', {
                method: 'POST',
                headers: tpGetHeaders(),
                body: JSON.stringify({ old_password: current, new_password: newPw })
            });
            const data = await res.json();
            if (res.ok) {
                setSuccess('Password changed successfully.');
                setTimeout(onSuccess, 1200);
            } else {
                setError(Object.values(data).flat().join(' '));
            }
        } catch {
            setError('Connection error. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="cp-modal-overlay" onClick={onClose}>
            <div className="cp-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
                <div className="cp-modal-header">
                    <h3>Reset Password</h3>
                    <button className="cp-modal-close" onClick={onClose}>&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="cp-modal-body">
                        {error && <div className="cp-login-error" style={{ marginBottom: 12 }}>{error}</div>}
                        {success && <div style={{ marginBottom: 12, color: '#15803d', background: '#dcfce7', padding: '10px 14px', borderRadius: 6 }}>{success}</div>}
                        <div className="form-group" style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: 5 }}>Current Password</label>
                            <input type="password" className="tmda-input" value={current} onChange={e => setCurrent(e.target.value)} required />
                        </div>
                        <div className="form-group" style={{ marginBottom: 16 }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: 5 }}>New Password</label>
                            <input type="password" className="tmda-input" value={newPw} onChange={e => setNewPw(e.target.value)} required />
                        </div>
                        <div className="form-group" style={{ marginBottom: 8 }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#475569', marginBottom: 5 }}>Confirm New Password</label>
                            <input type="password" className="tmda-input" value={confirm} onChange={e => setConfirm(e.target.value)} required />
                        </div>
                    </div>
                    <div className="cp-modal-footer">
                        <button type="button" className="cp-btn cp-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" className="cp-btn cp-btn-primary" disabled={saving || success}>{saving ? 'Changing…' : 'Change Password'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ── Main portal layout ────────────────────────────────────────────────────────
const TransporterPortalLayout = ({ onLogout }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [user, setUser] = useState(null);
    const [permissions, setPermissions] = useState(null);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const menuRef = React.useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    const pageTitles = {
        'eir': 'EIR',
        'invoicing': 'Invoicing',
    };
    const currentKey = Object.keys(pageTitles).find(k => location.pathname.includes(k));
    const pageTitle = pageTitles[currentKey] || 'Transporter Portal';

    const canView = (module) => permissions && permissions[module]?.can_view !== false;
    const firstAllowed = useMemo(() => {
        const item = NAV.flatMap(g => g.items).find(i => canView(i.module));
        return item ? item.path : null;
    }, [permissions]);

    const NoAccess = () => {
        useEffect(() => { onLogout(); }, [onLogout]);
        return <div className="cp-loader" style={{ padding: 40 }}>No access. Logging out…</div>;
    };

    const ProtectedRoute = ({ module, children }) => {
        if (!permissionsLoaded) {
            return <div className="cp-loader" style={{ padding: 40 }}>Loading…</div>;
        }
        if (!canView(module)) {
            return firstAllowed
                ? <Navigate to={`/transporter/${firstAllowed}`} replace />
                : <NoAccess />;
        }
        return children;
    };

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) {
            setPermissionsLoaded(true);
            return;
        }
        fetch('/api/transporter/me/', {
            headers: { 'Authorization': `Bearer ${token}` },
        }).then(r => r.ok ? r.json() : null).then(data => {
            if (data) {
                setUser(data);
                setPermissions(data.permissions || {});
                localStorage.setItem('tp_permissions', JSON.stringify(data.permissions || {}));
            }
        }).catch(() => {}).finally(() => setPermissionsLoaded(true));
    }, []);

    useEffect(() => {
        const closeMenu = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) document.addEventListener('mousedown', closeMenu);
        return () => document.removeEventListener('mousedown', closeMenu);
    }, [menuOpen]);

    return (
        <div className="cp-root">
            <Sidebar collapsed={collapsed} permissions={permissions} />
            <div className={`cp-main${collapsed ? ' collapsed' : ''}`}>
                <header className="cp-topbar">
                    <button
                        className="cp-topbar-toggle"
                        onClick={() => setCollapsed(c => !c)}
                        title="Toggle sidebar"
                    >
                        <i className="fa-solid fa-bars"></i>
                    </button>
                    <span className="cp-topbar-title">{pageTitle}</span>
                    <div className="cp-topbar-user" ref={menuRef} style={{ position: 'relative' }}>
                        <div
                            className="cp-user-trigger"
                            onClick={() => setMenuOpen(o => !o)}
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        >
                            <i className="fa-solid fa-user" style={{ marginRight: 6, color: '#94a3b8' }}></i>
                            {user?.full_name || user?.username || ''}
                            <i className="fa-solid fa-caret-down" style={{ marginLeft: 8 }}></i>
                        </div>
                        {menuOpen && (
                            <div className="cp-user-dropdown">
                                <button className="cp-dropdown-item" onClick={() => { setMenuOpen(false); setShowReset(true); }}>
                                    <i className="fa-solid fa-lock"></i> Reset Password
                                </button>
                                <div className="cp-dropdown-divider"></div>
                                <button className="cp-dropdown-item" onClick={() => { setMenuOpen(false); onLogout(); }}>
                                    <i className="fa-solid fa-right-from-bracket"></i> Sign Out
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="cp-content">
                    <Routes>
                        <Route path="eir" element={<TransporterEIR />} />
                        <Route path="invoicing" element={<TransporterInvoicing />} />
                        <Route path="*" element={<Navigate to={firstAllowed} replace />} />
                    </Routes>
                </main>
            </div>
            {showReset && (
                <TransporterResetPasswordModal
                    onClose={() => setShowReset(false)}
                    onSuccess={() => {
                        setShowReset(false);
                        const target = firstAllowed ? `/transporter/${firstAllowed}` : '/transporter';
                        if (!location.pathname.includes(target)) {
                            navigate(target);
                        }
                    }}
                />
            )}
        </div>
    );
};

// ── Root transporter portal app ───────────────────────────────────────────────
const TransporterPortalApp = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem('tp_permissions');
        setIsAuthenticated(false);
        navigate('/transporter', { replace: true });
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        const refresh = localStorage.getItem(REFRESH_KEY);
        if (!token || !refresh) {
            setAuthChecked(true);
            return;
        }
        fetch('/api/login/refresh/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh }),
        }).then(r => r.ok ? r.json() : null).then(data => {
            if (data?.access) {
                localStorage.setItem(TOKEN_KEY, data.access);
                if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
                setIsAuthenticated(true);
            } else {
                handleLogout();
            }
        }).catch(() => handleLogout()).finally(() => setAuthChecked(true));
    }, [handleLogout]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const iv = setInterval(() => {
            const refresh = localStorage.getItem(REFRESH_KEY);
            if (!refresh) return;
            fetch('/api/login/refresh/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh }),
            }).then(r => r.ok ? r.json() : null).then(data => {
                if (data?.access) {
                    localStorage.setItem(TOKEN_KEY, data.access);
                    if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
                } else {
                    handleLogout();
                }
            }).catch(() => handleLogout());
        }, 4 * 60 * 1000);
        return () => clearInterval(iv);
    }, [isAuthenticated, handleLogout]);

    const handleLoginSuccess = (data) => {
        localStorage.setItem(TOKEN_KEY, data.access);
        if (data.refresh) localStorage.setItem(REFRESH_KEY, data.refresh);
        localStorage.setItem(USER_KEY, data.user);
        if (data.permissions) {
            localStorage.setItem('tp_permissions', JSON.stringify(data.permissions));
        }
        setIsAuthenticated(true);
        navigate('/transporter', { replace: true });
    };

    if (!authChecked) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                Loading...
            </div>
        );
    }

    return (
        <Routes>
            <Route
                path="login"
                element={
                    isAuthenticated
                        ? <Navigate to="/transporter" replace />
                        : <TransporterLoginPage onLoginSuccess={handleLoginSuccess} />
                }
            />
            <Route
                path="*"
                element={
                    isAuthenticated
                        ? <TransporterPortalLayout onLogout={handleLogout} />
                        : <Navigate to="/transporter/login" replace />
                }
            />
        </Routes>
    );
};

export default TransporterPortalApp;
